import React, { useState, useCallback } from "react";

import { CircularProgress, Typography } from "@mui/material";
import RCUpload from "rc-upload";
import { useTranslation, Trans } from "react-i18next";
import Message from "../../Message/Message";
import { Box } from "../../../common/components/Box";
import { Icon } from "../../../common/components/Icon";
import { Link } from "../../../common/components/Link";
import { createStyles, palette, spacing } from "../../../styles/theme";

const useStyles = (loading: boolean, uploadSuccess: boolean) =>
  createStyles({
    dropZone: {
      height: 156,
      border: `1px dashed ${palette.grey.darkGrey}`,
      borderRadius: "12px",
      padding: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      px: "20px",
      gap: { xs: spacing.xsMobile, lg: spacing.xsDesktop },
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      opacity: 1,
      backgroundColor:
        loading || uploadSuccess ? palette.grey.lightBackground : "transparent",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        borderColor: palette.grey.darkerGrey,
      },
      "&:active": {
        borderColor: {
          xs: palette.brand.secondary,
          lg: palette.grey.darkerGrey,
        },
        backgroundColor: {
          xs: "transparent",
          lg: palette.grey.lightBackground,
        },
      },
      pointerEvents: loading ? "none" : "auto",
    },
  });

interface Props {
  onStart?: (file: any) => Promise<void>;
  onUpload: (options: any) => void;
  onSuccess?: (res: any, file: any) => Promise<void>;
  errorCode: string | null;
  acceptedFilesTitle: string;
  acceptedFileExtensions?: string[];
}

const UploadImageDropZone = (props: Props) => {
  const { t, i18n } = useTranslation();
  const {
    onStart,
    onUpload,
    onSuccess,
    errorCode,
    acceptedFilesTitle,
    acceptedFileExtensions = [".eps, .svg"],
  } = props;

  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const styles = useStyles(loading, uploadSuccess);

  const acceptedFileExtensionsString = acceptedFileExtensions.join(", ");

  const customRequest = useCallback(
    (options: any) => {
      onUpload(options);
    },
    [onUpload]
  );

  const rcUploadProps = {
    multiple: false,
    accept: acceptedFileExtensionsString,
    onStart: async (file: any) => {
      if (onStart) {
        await onStart(file);
      }
      setUploadSuccess(false);
      setLoading(true);
    },
    onSuccess: async (res: any, file: any) => {
      if (onSuccess) {
        await onSuccess(res, file);
      }
      setLoading(false);
      setUploadSuccess(true);
      setUploadedFileName(file.name);
    },
    onError: () => {
      setLoading(false);
      setUploadSuccess(false);
    },
    customRequest: customRequest,
    component: "div",
  };

  return (
    <React.Fragment>
      <Typography variant="textRegular" color={palette.grey.primary}>
        {acceptedFilesTitle}
      </Typography>
      {errorCode && (
        <Message
          title={t("assetManager.errors." + errorCode + ".title")}
          message={t("assetManager.errors." + errorCode + ".message")}
          linkText={
            i18n.exists("assetManager.errors." + errorCode + ".linkText")
              ? t("assetManager.errors." + errorCode + ".linkText")
              : ""
          }
          linkUrl={
            i18n.exists("assetManager.errors." + errorCode + ".linkUrl")
              ? t("assetManager.errors." + errorCode + ".linkUrl")
              : undefined
          }
        />
      )}
      <RCUpload {...rcUploadProps}>
        <Box sx={styles.dropZone}>
          {loading ? (
            <CircularProgress />
          ) : uploadSuccess && uploadedFileName ? (
            <React.Fragment>
              <Icon icon="check_circle" size={24} color={palette.misc.green} />
              <Typography variant="textRegular" textAlign={"center"}>
                {uploadedFileName}
              </Typography>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Icon icon="upload" size={24} />
              <Typography variant="textRegular" textAlign={"center"}>
                <Trans
                  i18nKey="techpack.upload_file_select"
                  components={[
                    <Link
                      alwaysUnderline
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    />,
                  ]}
                >
                  {t("techpack.upload_file_select")}
                </Trans>
              </Typography>
              <Typography variant="textSm" color={palette.grey.primary}>
                ({acceptedFileExtensionsString})
              </Typography>
            </React.Fragment>
          )}

          {errorCode && (
            <Typography variant="textSm" color="error" sx={{ mt: 1 }}>
              {t(`${errorCode}`, { defaultValue: t("errors.general") })}
            </Typography>
          )}
        </Box>
      </RCUpload>
    </React.Fragment>
  );
};

export default UploadImageDropZone;
